/**
 * ChannelLogoPairNew Component
 * 
 * Displays Msool logo paired with a channel logo, with optional connector line.
 * Replaces the old ChannelLogoPair component with standardized sizing.
 */

import { FC, memo, useMemo } from 'react';
import styled from 'styled-components';
import { IChannelLogoPairProps, LogoSize } from './types';
import { LOGO_SIZES, CHANNEL_LIGHT_BACKGROUNDS, BORDER_RADIUS } from './constants';
import MsoolLogo from './MsoolLogo';
import ChannelLogo from './ChannelLogo';

interface IContainerProps {
  $connectorWidth: number;
}

const Container = styled.div`
  display: flex;
  align-items: center;
`;

const Connector = styled.div<{ $width: number; $isConnected?: boolean }>`
  width: ${(props) => props.$width}px;
  height: 4px;
  background: ${(props) => (props.$isConnected ? '#12B76A' : '#D0D5DD')};
  position: relative;
  margin: 0 -8px;
  z-index: 0;

  ${(props) =>
    props.$isConnected &&
    `
    &::after {
      content: '✓';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 20px;
      height: 20px;
      background: #12B76A;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 10px;
      font-weight: bold;
    }
  `}
`;

/** Get connector width based on logo size */
const getConnectorWidth = (size: LogoSize | number): number => {
  const numericSize = typeof size === 'number' ? size : LOGO_SIZES[size];
  if (numericSize <= LOGO_SIZES.sm) return 20;
  if (numericSize <= LOGO_SIZES.md) return 28;
  if (numericSize <= LOGO_SIZES.lg) return 36;
  return 42;
};

/** Get numeric size from LogoSize or number */
const getSize = (size: LogoSize | number): number => {
  if (typeof size === 'number') return size;
  return LOGO_SIZES[size];
};

const ChannelLogoPairNew: FC<IChannelLogoPairProps> = memo(({
  channel,
  size = 'xl',
  showConnector = true,
  isConnected = false,
  className,
}) => {
  const numericSize = useMemo(() => getSize(size), [size]);
  const connectorWidth = useMemo(() => getConnectorWidth(size), [size]);

  return (
    <Container className={className}>
      <MsoolLogo size={numericSize} />
      {showConnector && (
        <Connector $width={connectorWidth} $isConnected={isConnected} />
      )}
      <ChannelLogo
        channel={channel}
        size={numericSize}
        showBackground
        backgroundColor={CHANNEL_LIGHT_BACKGROUNDS[channel]}
      />
    </Container>
  );
});

ChannelLogoPairNew.displayName = 'ChannelLogoPairNew';

export default ChannelLogoPairNew;
